/* eslint-disable react/no-danger */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO } from 'wdna-components';
import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { notice, NoticeListDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  showGrid?: boolean;
  update?: string;

  modalShow: boolean;
  modalData?: NoticeListDO;

  data?: NoticeListDO[];
}

export default class Notifications extends React.Component<Props, State> {
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.buttons = {
      info: true,
      del: true,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      modalShow: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    // TODO: filter by user company if not admin
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = user.type === 'adm';
    const req: any = { };

    this.loadingStart();
    notice<NoticeListDO[]>('list', undefined, req)
      ?.then((resp: NoticeListDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onDelete = (value: NoticeListDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      notice('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public onInfo = (value: NoticeListDO): void => {
    this.setState({
      update: window.getStr(),
      modalShow: true,
      modalData: value,
    });
  };

  public onCloseModal = (): void => {
    this.setState({
      update: window.getStr(),
      modalShow: false,
    });
  };

  public getTimeZoneString = (date: Date): string => {
    const hours: number = Math.trunc(-date.getTimezoneOffset() / 60);
    const minutes: number = -date.getTimezoneOffset() % 60;

    const timezoneString: string = `UTC ${hours < 0 ? '-' : '+'}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return timezoneString;
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid, modalShow, modalData } = this.state;

    const columnsToHide: string[] = [
      'body',
      'alarmId',
      'sensorId',
      'typeResultId',
    ];
    const columnAlias: Record<string, string> = {
      id: 'ID',
      type: 'Action',
      alarmId: 'Alarm Id',
      date: 'Date',
      dateSend: 'Date Sent',
      recipient: 'Recipient',
      subject: 'Subject',
      company: 'Company',
      sensorCode: 'Sensor Code',
      sensorName: 'Sensor Name',
      typeResultName: 'Result',
    };
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      type: { textAlign: 'left' },
      date: { textAlign: 'center' },
      dateSend: { textAlign: 'center' },
      recipient: { textAlign: 'left' },
      subject: { textAlign: 'left' },
      company: { textAlign: 'left' },
      sensorCode: { textAlign: 'right' },
      sensorName: { textAlign: 'left' },
      typeResultName: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      recipient: { minWidth: 250 },
      subject: { minWidth: 250 },
      type: { minWidth: 80, maxWidth: 80 },
      typeResultName: { minWidth: 80, maxWidth: 80 },
    };

    const date: Date = new Date(modalData?.date || '');
    const dateString: string = `${date.toLocaleString()} ${this.getTimeZoneString(date)}`;
    const dateSend: Date = new Date(modalData?.dateSend || '');
    const dateSendString: string = `${dateSend.toLocaleString()} ${this.getTimeZoneString(dateSend)}`;

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          customColumnsDefs={customColumnsDefs}
          buttons={this.buttons}
          onInfo={this.onInfo}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          pagination={false}
          showRowNumber
        />
        <ContainerModal
          fullScreenButton={false}
          show={modalShow}
          title={`Warning details - ${modalData?.id || ''}`}
          modalClass="modal-small selector-modal"
          onClose={this.onCloseModal}
          footerButtons={[
            {
              text: 'OK',
              className: 'btn btn-primary',
              onClick: this.onCloseModal,
            },
          ]}
        >
          <b>Company: </b> {modalData && modalData.company} <br />
          <b>Recipient: </b> {modalData && modalData.recipient} <br />
          <b>Subject: </b> {modalData && modalData.subject} <br />
          <b>Date of incidence: </b> {modalData && dateString} <br />
          <b>Date of notice: </b> {modalData && dateSendString} <br /> <br />
          <div dangerouslySetInnerHTML={{ __html: modalData?.body || '' }} />
        </ContainerModal>
      </div>
    );
  }
}
